import classNames from 'classnames';
import { PrismicNextImage } from '@prismicio/next';
import { asText, Content, isFilled } from '@prismicio/client';

import {
  Box,
  Card,
  Heading,
  AutoGrid,
  CardFooter,
  CardHeader,
  Typography,
  CardContent,
} from '@preventx/oxygen';
import { RichText } from '@preventx/prismic-services';

// Services
import { applicationEnv } from '@/services/env/shared';

// Components
import { Link } from '@/components/Actions/Link';

type WithImageFeaturesProps = {
  sliceId: string;
  slice: Content.RegionSliceWithImageFeatures;
};

export const WithImageFeatures = ({
  slice,
  sliceId,
}: WithImageFeaturesProps) => {
  const { primary } = slice;
  const { title, caption, features } = primary;

  const { WebsiteUrl } = applicationEnv();

  return (
    <>
      <Box component="div" className="headline align-center">
        <Box component="div" addFlow>
          <Heading component="h2" addHeadlineTitle>
            {asText(title)}
          </Heading>

          <Typography component="p" className="headline__sub">
            {asText(caption)}
          </Typography>
        </Box>
      </Box>

      <AutoGrid component="div" placement="auto-fit">
        {(features ?? []).map((feature, index) => (
          <Card
            component="article"
            key={`region-image-features-${sliceId}-${index + 1}`}
          >
            {isFilled.image(feature.image) && (
              <Box
                component="div"
                className={classNames({
                  'ratio-auto': feature.image_aspect_ratio === 'Auto',
                  card__hero: feature.image_aspect_ratio === 'Default',
                  'ratio-golden': feature.image_aspect_ratio === 'Golden',
                  'ratio-square': feature.image_aspect_ratio === 'Square',
                  'ratio-portrait': feature.image_aspect_ratio === 'Portrait',

                  'ratio-ultrawide': feature.image_aspect_ratio === 'Ultrawide',
                  'ratio-landscape': feature.image_aspect_ratio === 'Landscape',
                  'ratio-widescreen':
                    feature.image_aspect_ratio === 'Widescreen',
                })}
              >
                <PrismicNextImage className="w-full" field={feature.image} />
              </Box>
            )}

            <CardHeader addSROnly={feature.is_title_hidden}>
              <Heading component="h3">{asText(feature.title)}</Heading>
            </CardHeader>

            {isFilled.richText(feature.content) && (
              <CardContent addFlow>
                <RichText
                  variant="default"
                  websiteUrl={WebsiteUrl}
                  content={feature.content}
                />
              </CardContent>
            )}

            {isFilled.link(feature.link) && (
              <CardFooter>
                <Link
                  as="default"
                  type="button"
                  retainSearchParams
                  href={feature.link}
                  className="card__action"
                >
                  {feature.link.text}
                </Link>
              </CardFooter>
            )}
          </Card>
        ))}
      </AutoGrid>
    </>
  );
};
